import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import moment from "moment";
import { Doughnut, Bar } from "react-chartjs-2";
import { Button, DatePicker, Select, Table, Tooltip } from "antd";
import ReportsDiamondService from "../../../services/reports-Diamond.service";
import { BlobToDownloadFile } from "../../../helper/commanFunction";
import { bucketTopN } from "./_shared/bucketTopN";
import CustomLegend from "./_shared/CustomLegend";
import ChartState from "./_shared/ChartState";
import { donutOptions, barOptions } from "./_shared/chartTheme";
import { exportChartPdf } from "./_shared/exportChartPdf";

const { RangePicker } = DatePicker;
const { Option } = Select;

// Not one of the 6 single-attribute reports (Story 1658-1665) — this is the
// older vendor-click-distribution dashboard (2 charts + 2 differently-shaped
// tables, own backend endpoints), so it doesn't fit ReportScaffold's
// single-dataKey config. Restyled in place for visual parity with the rest
// of the suite per explicit user request, keeping all API calls unchanged.
const DiamondReportsChartsIndComp = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const initialGlobalDiamondState = {
    dataRangeFrom: moment().subtract(30, "days").format("YYYY-MM-DD"),
    dataRangeTo: moment().format("YYYY-MM-DD"),
    SelectedDiamondValue: "All",
    retailerIds: [],
    vendorIds: [],
  };

  const [globalDiamonds, setGlobalDiamond] = useState(initialGlobalDiamondState);
  const [dealerId, setDealerId] = useState();
  const [forDealer, setForDealer] = useState("");

  const [vendorClicksList, setVendorClicksList] = useState([]);
  const [LoadChartReportsLoading, setLoadChartReportsLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pctChartType, setPctChartType] = useState("doughnut");

  const [TableChartList, setTableChartList] = useState([]);
  const [TableChartLoading, setTableChartLoading] = useState(false);

  const [TableDiamondChartList, setTableDiamondChartList] = useState([]);
  const [TableForDiamChartLoading, setTableForDiamChartLoading] = useState(false);
  const [recentSearch, setRecentSearch] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalCount, setTotalCount] = useState(0);
  const [EventsCount, setEventsCount] = useState(0);
  const [TotalEventsCount, setTotalEventsCount] = useState(0);
  const [sortType, setSortType] = useState("DESC");
  const [sortColumnName, setSortColumnName] = useState("GT.MODIFIED");
  const [downloadCSVDataloading, setDownloadCSVDataloading] = useState(false);
  const [downloadPDFDataloading, setDownloadPDFDataloading] = useState(false);

  const onDateChange = (dates) => {
    if (dates) {
      setGlobalDiamond((prevState) => ({
        ...prevState,
        dataRangeFrom: moment.utc(dates[0]).toISOString(),
        dataRangeTo: moment.utc(dates[1]).toISOString(),
      }));
    }
  };

  const onDiamondTypeChange = (e, name) => {
    setGlobalDiamond({ ...globalDiamonds, [name]: e });
  };

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      setDealerId(loginDetials.loginDetials.responseData.dealerId);
      handleCheckDealermembers(loginDetials.loginDetials.responseData.dealerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCheckDealermembers = (id) => {
    ReportsDiamondService.CheckDealermembers({ dealerID: id })
      .then((response) => {
        if (response.data.message === "Success") {
          let fd;
          if (
            loginDetials.loginDetials.responseData.dealerMemberTypeName === "VendorDiamond" ||
            loginDetials.loginDetials.responseData.dealerMemberTypeName === "Vendor"
          ) {
            fd = 1;
          } else if (
            loginDetials.loginDetials.responseData.dealerMemberTypeName === "Retailer" ||
            loginDetials.loginDetials.responseData.dealerMemberTypeName === "Combined"
          ) {
            fd = 2;
          } else {
            fd = 3;
          }
          setForDealer(fd || "");
          handleGetDiamondColumnChartData(id, fd);
          handleGetDiamondReportForTableChart(id, fd);
          handleDimondChart_SortingBindData(id, fd, 1, pageSize, sortColumnName, sortType);
        } else {
          setForDealer("");
        }
      })
      .catch(() => setForDealer(""));
  };

  const handleGetDiamondColumnChartData = (dealerIdArg, forDealerArg) => {
    let inputData = {
      dealerID: dealerIdArg.toString(),
      forDealer: forDealerArg ? forDealerArg.toString() : "",
      fromDate: globalDiamonds.dataRangeFrom ? globalDiamonds.dataRangeFrom : "2020-08-17",
      toDate: globalDiamonds.dataRangeTo ? globalDiamonds.dataRangeTo : "2022-09-15",
      selectedDiamondValue: globalDiamonds.SelectedDiamondValue,
    };
    setLoadChartReportsLoading(true);
    ReportsDiamondService.GetDiamondColumnChartData(inputData)
      .then((response) => {
        setLoadChartReportsLoading(false);
        const responseData = response.data.responseData;
        if (response.data.message === "Success" && responseData && Object.keys(responseData).length !== 0) {
          setVendorClicksList(responseData.Table || []);
        } else {
          setVendorClicksList([]);
        }
      })
      .catch(() => {
        setLoadChartReportsLoading(false);
        setVendorClicksList([]);
      });
  };

  const handleGetDiamondReportForTableChart = (dealerIdArg, forDealerArg) => {
    let inputData = {
      dealerID: dealerIdArg.toString(),
      forDealer: forDealerArg ? forDealerArg.toString() : "",
      fromDate: globalDiamonds.dataRangeFrom
        ? moment(globalDiamonds.dataRangeFrom).format("YYYY-MM-DD")
        : "2020-08-17",
      toDate: globalDiamonds.dataRangeTo
        ? moment(globalDiamonds.dataRangeTo).format("YYYY-MM-DD")
        : "2022-09-15",
      selectedDiamondValue: globalDiamonds.SelectedDiamondValue,
    };
    setTableChartLoading(true);
    ReportsDiamondService.GetDiamondReportForTableChart(inputData)
      .then((response) => {
        setTableChartLoading(false);
        const responseData = response.data.responseData;
        if (
          response.data.message === "Success" &&
          responseData &&
          Object.keys(responseData).length !== 0 &&
          responseData.dtData?.length > 0
        ) {
          setTableChartList(responseData.dtData);
        } else {
          setTableChartList([]);
        }
      })
      .catch(() => {
        setTableChartLoading(false);
        setTableChartList([]);
      });
  };

  const handleDimondChart_SortingBindData = (
    dealerIdArg,
    forDealerArg,
    pageNoArg,
    pageSizeArg,
    sortColumnNameArg,
    sortOrderArg
  ) => {
    let inputData = {
      dealerID: dealerIdArg,
      forDealer: forDealerArg ? forDealerArg.toString() : "",
      orderBy: "",
      pagging: 0,
      fromDate: globalDiamonds.dataRangeFrom
        ? moment(globalDiamonds.dataRangeFrom).format("YYYY-MM-DD")
        : "2020-08-17",
      toDate: globalDiamonds.dataRangeTo
        ? moment(globalDiamonds.dataRangeTo).format("YYYY-MM-DD")
        : "2022-09-15",
      lastAccessDate: globalDiamonds.dataRangeTo
        ? moment(globalDiamonds.dataRangeTo).format("YYYY-MM-DD")
        : "2022-09-15",
      noOfRows: 0,
      selectedDiamondValue: globalDiamonds.SelectedDiamondValue,
      pageNumber: pageNoArg || 1,
      pageSize: pageSizeArg || 15,
      sortOrderCol: sortColumnNameArg || "GT.MODIFIED",
      sortOrderType: sortOrderArg || "DESC",
    };
    setTableForDiamChartLoading(true);
    ReportsDiamondService.DimondChart_SortingBindData(inputData)
      .then((response) => {
        setTableForDiamChartLoading(false);
        const responseData = response.data.responseData;
        const list = responseData?.TableData?.Table || [];
        const total = list.length > 0 ? list[0].TotalCount : 0;
        if (response.data.message === "Success" && responseData && Object.keys(responseData).length !== 0) {
          setTableDiamondChartList(list);
          setEventsCount(list.length);
          setTotalCount(total);
          setTotalEventsCount(total);
        } else {
          setTotalCount(0);
          setTableDiamondChartList([]);
          setEventsCount(0);
          setTotalEventsCount(0);
        }
      })
      .catch(() => {
        setTableForDiamChartLoading(false);
        setTotalCount(0);
        setTableDiamondChartList([]);
      });
  };

  const handleExportToCSV = (forDealerArg) => {
    let inputData = {
      dealerID: dealerId.toString(),
      hdnFromDate: globalDiamonds.dataRangeFrom,
      hdnToDate: globalDiamonds.dataRangeTo,
      hdnPaggingRun: "",
      hdnForDealer: forDealerArg.toString(),
      hdnRetailerFilterId: "",
      hdnOrderBy: "",
      dealerCOMPANY: "",
      checkVendorMembership: "",
      selectedDiamondValue: globalDiamonds.SelectedDiamondValue,
    };
    setDownloadCSVDataloading(true);
    ReportsDiamondService.ExportToCSV(inputData)
      .then((response) => {
        setDownloadCSVDataloading(false);
        const fileName = `${loginDetials.loginDetials.responseData.dealerCompany.replace(/\s+/g, "")}_DiamondChartReports_${new Date()
          .toJSON()
          .slice(0, 10)
          .split("-")
          .slice(1)
          .concat(new Date().toJSON().slice(0, 4))
          .join("_")}.csv`;
        BlobToDownloadFile(response.data, fileName);
      })
      .catch(() => setDownloadCSVDataloading(false));
  };

  const handleExportToPDF = (forDealerArg) => {
    let inputData = {
      dealerID: dealerId,
      forDealer: forDealerArg ? forDealerArg.toString() : "",
      fromDate: globalDiamonds.dataRangeFrom
        ? moment(globalDiamonds.dataRangeFrom).format("YYYY-MM-DD")
        : "2020-08-17",
      toDate: globalDiamonds.dataRangeTo
        ? moment(globalDiamonds.dataRangeTo).format("YYYY-MM-DD")
        : "2022-09-15",
      lastAccessDate: globalDiamonds.dataRangeTo
        ? moment(globalDiamonds.dataRangeTo).format("YYYY-MM-DD")
        : "2022-09-15",
      noOfRows: 0,
      orderBy: "",
      pageNumber: 1,
      pageSize: 15,
      pagging: 0,
      selectedDiamondValue: globalDiamonds.SelectedDiamondValue,
      sortOrderCol: "GT.MODIFIED",
      sortOrderType: "ASC",
    };
    setDownloadPDFDataloading(true);
    ReportsDiamondService.ExportTableDataToPDF(inputData)
      .then((response) => {
        setDownloadPDFDataloading(false);
        const fileName = "Chart" + new Date().toJSON().slice(0, 10).split("-").reverse().join("/") + ".pdf";
        BlobToDownloadFile(response.data, fileName, "pdf");
      })
      .catch(() => setDownloadPDFDataloading(false));
  };

  const handleTableASCDES = (pagination, _filters, sorter) => {
    const tableBody = document.querySelector(".edr-recent-table .ant-table-body");
    if (tableBody) tableBody.scrollTop = 0;

    let type = "DESC";
    let column = "GT.MODIFIED";
    if (sorter.order === "descend") {
      type = "DESC";
      column = sorter.field;
    } else if (sorter.order === "ascend") {
      type = "ASC";
      column = sorter.field;
    }

    setSortType(type);
    setSortColumnName(column);
    setPageNo(pagination.current);
    setPageSize(pagination.pageSize);
    handleDimondChart_SortingBindData(dealerId, forDealer, pagination.current, pagination.pageSize, column, type);
  };

  const handleApplyFilter = () => {
    handleGetDiamondColumnChartData(dealerId, forDealer);
    handleGetDiamondReportForTableChart(dealerId, forDealer);
    setPageNo(1);
    handleDimondChart_SortingBindData(dealerId, forDealer, 1, pageSize, sortColumnName, sortType);
  };

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: "Diamond Clicks Report",
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom: globalDiamonds.dataRangeFrom,
        dataRangeTo: globalDiamonds.dataRangeTo,
        selectedDiamondValue: globalDiamonds.SelectedDiamondValue,
        totalClicks: TotalEventsCount,
        fileName: "Diamond-Clicks-Search-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const { chartData, colors } = bucketTopN(vendorClicksList, {
    labelKey: "DealerCompany",
    valueKey: "count",
    chartLabel: "Vendor",
    othersLabel: t("Others"),
  });
  const rows = chartData.slice(1);
  const barChartJsData = {
    labels: rows.map((row) => row[0]),
    datasets: [{ data: rows.map((row) => row[1]), backgroundColor: colors, borderRadius: 4 }],
  };
  const pctChartJsData = {
    labels: rows.map((row) => row[0]),
    datasets: [
      {
        data: rows.map((row) => row[1]),
        backgroundColor: colors,
        borderColor: "#fff",
        borderWidth: pctChartType === "doughnut" ? 2 : 0,
        borderRadius: pctChartType === "bar" ? 4 : 0,
      },
    ],
  };

  const visibleRecentRows = useMemo(() => {
    if (!recentSearch.trim()) return TableDiamondChartList;
    const q = recentSearch.trim().toLowerCase();
    return TableDiamondChartList.filter((row) =>
      Object.values(row).some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [TableDiamondChartList, recentSearch]);

  return (
    <React.Fragment>
      <div className="pendjewelreq__section form__fields">
        <div className="row magento__mapping">
          <div className="col-md-12">
            <div className="edr-pagehead-row">
              <div className="edr-pagehead">
                <h1 className="edr-pagehead-title">{t("Diamond Charts")}</h1>
                <div className="edr-pagehead-sub">
                  {moment(globalDiamonds.dataRangeFrom).format("MMM D, YYYY")}
                  {" – "}
                  {moment(globalDiamonds.dataRangeTo).format("MMM D, YYYY")}
                </div>
              </div>
              <div className="edr-pagehead-actions">
                {rows.length > 0 && (
                  <Button className="ghost-btn" onClick={handleDownloadChart} loading={pdfLoading}>
                    <i className="fa fa-download"></i> {t("Download Chart")}
                  </Button>
                )}
                <button className="primary-btn" onClick={handleApplyFilter}>
                  {t("Apply Filter")}
                </button>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="row edr-filters">
              <div className="col-md-3">
                <div className="input__block dataRange-input-block datepicker__block">
                  <label>{t("Date Range")}</label>
                  <RangePicker
                    className="date-range-input"
                    ranges={{
                      Today: [moment(), moment()],
                      Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
                      "Last 7 Days": [moment().subtract(7, "days"), moment()],
                      "Last 30 Days": [moment().subtract(30, "days"), moment()],
                      "This Month": [moment().startOf("month"), moment().endOf("month")],
                      "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "months").endOf("month")],
                      "Last 90 Days": [moment().subtract(90, "days"), moment()],
                      "Year To Date": [moment().startOf("year"), moment()],
                    }}
                    format={"MM-DD-YYYY"}
                    onChange={onDateChange}
                    defaultValue={[moment().subtract(30, "days"), moment()]}
                    defaultPickerValue={[moment().subtract(30, "days"), moment()]}
                  />
                </div>
              </div>

              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Select Diamond")}</label>
                  <Select
                    className="border__grey"
                    placeholder={t("All")}
                    showSearch
                    name="SelectedDiamondValue"
                    optionFilterProp="children"
                    value={globalDiamonds.SelectedDiamondValue || null}
                    onChange={(e) => onDiamondTypeChange(e, "SelectedDiamondValue")}
                  >
                    <Option value="All">{t("All")}</Option>
                    <Option value="Mined">{t("Mined Diamond")}</Option>
                    <Option value="Lab">{t("Lab Grown Diamond")}</Option>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="edr-kpis">
              <div className="edr-kpi">
                <div className="edr-kpi-lab">{t("Total Clicks")}</div>
                <div className="edr-kpi-val">{TotalEventsCount?.toLocaleString?.() ?? TotalEventsCount ?? 0}</div>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="edr-grid2" id="pdf-content">
              <div className="edr-panel mt-4">
                <label>
                  <b> {t("Vendor Diamond Clicks Distribution")} </b>
                </label>
                <div className="chart__section">
                  {!LoadChartReportsLoading && rows.length > 0 ? (
                    <div className="edr-chartwrap edr-tall">
                      <Bar data={barChartJsData} options={barOptions(true)} />
                    </div>
                  ) : (
                    <ChartState loading={LoadChartReportsLoading} />
                  )}
                </div>
              </div>
              <div className="edr-panel mt-4">
                <div className="edr-panel-head">
                  <label>
                    <b> {t("Vendor Diamond Clicks Percentage")} </b>
                  </label>
                  <div className="edr-seg">
                    <button
                      className={pctChartType === "doughnut" ? "edr-on" : ""}
                      onClick={() => setPctChartType("doughnut")}
                    >
                      {t("Donut")}
                    </button>
                    <button
                      className={pctChartType === "bar" ? "edr-on" : ""}
                      onClick={() => setPctChartType("bar")}
                    >
                      {t("Bar")}
                    </button>
                  </div>
                </div>
                <div
                  className="chart__section"
                  style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}
                >
                  {!LoadChartReportsLoading && rows.length > 0 ? (
                    <>
                      {pctChartType === "doughnut" && <CustomLegend chartData={chartData} colorData={colors} />}
                      <div className="edr-chartwrap">
                        {pctChartType === "doughnut" ? (
                          <Doughnut data={pctChartJsData} options={donutOptions} />
                        ) : (
                          <Bar data={pctChartJsData} options={barOptions(true)} />
                        )}
                      </div>
                    </>
                  ) : (
                    <ChartState loading={LoadChartReportsLoading} />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="edr-panel mt-4">
              <div className="edr-panel-head">
                <div>
                  <h3>{t("Top Diamond Attribute Values")}</h3>
                  <div className="edr-panel-desc">{t("Highest-value diamond per attribute combination")}</div>
                </div>
              </div>
              <div className="edr-detail-table">
                <Table
                  className="pendjewelreq__tbl"
                  scroll={{ x: 1200, y: "calc(100vh - 340px)" }}
                  loading={TableChartLoading}
                  columns={[
                    { title: <Tooltip title={t("Date")}>{t("Date")}</Tooltip>, ellipsis: true, dataIndex: "Date", defaultSortOrder: "descend", sorter: () => {} },
                    { title: <Tooltip title={t("Shape")}>{t("Shape")}</Tooltip>, ellipsis: true, dataIndex: "shape" },
                    { title: <Tooltip title={t("Size")}>{t("Size")}</Tooltip>, ellipsis: true, dataIndex: "Size" },
                    { title: <Tooltip title={t("Color")}>{t("Color")}</Tooltip>, ellipsis: true, dataIndex: "Color" },
                    { title: <Tooltip title={t("Cut")}>{t("Cut")}</Tooltip>, ellipsis: true, dataIndex: "CutGrade" },
                    { title: <Tooltip title={t("Clarity")}>{t("Clarity")}</Tooltip>, ellipsis: true, dataIndex: "Clarity" },
                    {
                      title: <Tooltip title={t("Price")}>{t("Price")}</Tooltip>,
                      ellipsis: true,
                      dataIndex: "Price",
                      render: (row, item) => {
                        const value = item.Price ? item.Price : "Call For Price";
                        return (
                          <Tooltip title={value}>
                            <span>{value}</span>
                          </Tooltip>
                        );
                      },
                    },
                  ]}
                  dataSource={TableChartList}
                />
              </div>
            </div>
          </div>

          <div className="col-md-12">
            <div className="edr-panel mt-4">
              <div className="edr-panel-head">
                <div>
                  <h3>{t("Recent Diamond Attribute Searches")}</h3>
                  <div className="edr-panel-desc">
                    {t("{{count}} / {{total}} events", { count: EventsCount, total: TotalEventsCount })}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button className="ghost-btn" onClick={() => handleExportToPDF(forDealer)} loading={downloadPDFDataloading}>
                    <i className="fa fa-download"></i> {t("PDF")}
                  </Button>
                  <Button className="ghost-btn" onClick={() => handleExportToCSV(forDealer)} loading={downloadCSVDataloading}>
                    <i className="fa fa-download"></i> {t("CSV")}
                  </Button>
                </div>
              </div>
              <div className="edr-tblbar">
                <input
                  className="edr-tblsearch"
                  placeholder={t("Search this page: IP, shape, color, cut…")}
                  value={recentSearch}
                  onChange={(e) => setRecentSearch(e.target.value)}
                />
                <span className="edr-badge">{t("{{count}} rows", { count: totalCount })}</span>
              </div>
              <div className="edr-detail-table edr-recent-table">
                <Table
                  className="pendjewelreq__tbl"
                  scroll={{ x: 1200, y: "calc(100vh - 340px)" }}
                  loading={TableForDiamChartLoading}
                  columns={[
                    { title: <Tooltip title={t("IPAddress")}>{t("IPAddress")}</Tooltip>, ellipsis: true, dataIndex: "UsersIPAddress", sorter: () => {} },
                    { title: <Tooltip title={t("Stock #")}>{t("Stock #")}</Tooltip>, ellipsis: true, dataIndex: "DealerStockNo", sorter: () => {} },
                    { title: <Tooltip title={t("Dealer Company")}>{t("Dealer Company")}</Tooltip>, ellipsis: true, dataIndex: "DealerCompany", sorter: () => {} },
                    { title: <Tooltip title={t("Shape")}>{t("Shape")}</Tooltip>, ellipsis: true, dataIndex: "Cut", sorter: () => {} },
                    { title: <Tooltip title={t("Size")}>{t("Size")}</Tooltip>, ellipsis: true, dataIndex: "Size", sorter: () => {} },
                    { title: <Tooltip title={t("Cut")}>{t("Cut")}</Tooltip>, ellipsis: true, dataIndex: "CutGrade", sorter: () => {} },
                    { title: <Tooltip title={t("Color")}>{t("Color")}</Tooltip>, ellipsis: true, dataIndex: "Color", sorter: () => {} },
                    { title: <Tooltip title={t("Clarity")}>{t("Clarity")}</Tooltip>, ellipsis: true, dataIndex: "Clarity", sorter: () => {} },
                    { title: <Tooltip title={t("Cert")}>{t("Cert")}</Tooltip>, ellipsis: true, dataIndex: "Certificate", sorter: () => {} },
                    {
                      title: <Tooltip title={t("Price")}>{t("Price")}</Tooltip>,
                      ellipsis: true,
                      dataIndex: "WholesalePrice",
                      sorter: () => {},
                      render: (row, item) => {
                        const value = item.WholesalePrice ? item.WholesalePrice : "Call For Price";
                        return (
                          <Tooltip title={value}>
                            <span>{value}</span>
                          </Tooltip>
                        );
                      },
                    },
                    {
                      title: <Tooltip title={t("Day & Time")}>{t("Day & Time")}</Tooltip>,
                      ellipsis: true,
                      dataIndex: "Modified",
                      defaultSortOrder: "descend",
                      sorter: (a, b) => new Date(a.Modified) - new Date(b.Modified),
                      render: (value) => {
                        if (!value) return null;
                        const formattedDate = moment(value).format("MM-DD-YYYY, hh:mm:ss A");
                        return (
                          <Tooltip title={formattedDate}>
                            <span>{formattedDate}</span>
                          </Tooltip>
                        );
                      },
                    },
                  ]}
                  dataSource={visibleRecentRows}
                  pagination={{
                    size: "small",
                    total: totalCount,
                    pageNo: pageNo,
                    pageSize: pageSize,
                    pageSizeOptions: [10, 15, 20, 50, 100],
                    responsive: true,
                    showSizeChanger: true,
                  }}
                  onChange={handleTableASCDES}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default DiamondReportsChartsIndComp;
